/* Copyright (c) 2020-present CloudIO Inc */

import { IoInstalledComponentsV } from '@cloudio-saas/datasource-types';
import { getIn, hasIn, removeIn, setIn } from '@cloudio-saas/mutx';
import { emptyFunction } from '@cloudio-saas/statex';
import { Page, Resource, ResourceType } from 'componentregistry';
import type { Platform } from 'componentregistry/platform/Platform';
import Controller from 'controller/Controller';
import { contextAppIdAtom, pageAtom } from 'designer/atoms';
import stringify from 'json-stable-stringify';
import { getErrorMessage } from 'utils/isEmpty';
import Logger from 'utils/Logger';
import { darkModeAtom } from 'utils/state';
import {
  pageInitializedSignal,
  startPageProgress,
} from '../components/Page/PageProgressed';
import {
  compile,
  CompiledInstalledComponent,
  loadInstalledComponents,
  setInstalledComponents,
} from './InstalledComponents';
import { setupUserAccess } from './useUser';

export const EMPTY_PAGE: Page = Object.freeze({
  root: {
    itemId: 'root',
    parent: 'root',
    type: 'Page',
    children: [],
    props: {
      // @ts-ignore metadata
      windowTitle: { value: 'Blank Page', type: 'text' },
    },
  },
}) as Page;

export const PAGE_404: Page = Object.freeze({
  root: {
    itemId: 'root',
    parent: 'root',
    type: 'Page',
    children: [],
    props: {
      // @ts-ignore metadata
      windowTitle: { value: '404', type: 'text' },
    },
  },
}) as Page;

type Callback = () => void;

async function onAppLoaded(platform: Platform, appUid: string): Promise<void> {
  const store = platform.getStore<IoInstalledComponentsV>(
    appUid,
    '$$Sidebar$$',
    'IoInstalledComponentsV',
  );
  if (!store) {
    throw Error('Missing store IoInstalledComponentsV');
  }
  const records = await store.query({});
  if (records) {
    let installedComponents = await Promise.all(
      Object.values(records).map((row) =>
        compile(platform, row as IoInstalledComponentsV),
      ),
    );
    installedComponents = installedComponents.filter((ic) => !!ic);
    setInstalledComponents(
      appUid,
      installedComponents as CompiledInstalledComponent[],
    );
    loadInstalledComponents(platform, appUid);
  }
}

async function onAppSwitched(
  platform: Platform,
  appUid: string,
): Promise<void> {
  loadInstalledComponents(platform, appUid);
}

function wrapPromise<T>(
  platform: Platform,
  ds: string,
  appUid: string,
  pk: Record<string, string>,
): ResourceType<T> {
  let status = 'pending';
  let result: T;
  let error: Error;
  let callback = emptyFunction;
  if (ds === 'IoPages') {
    pageInitializedSignal[pk.appUid][pk.pageUid].set(false);
  }
  let end = emptyFunction;
  if (ds === 'IoPages') {
    end = startPageProgress(pk.appUid, pk.pageUid, 'FetchAtom');
  }
  const promise: Promise<T> = platform
    .sendRequest(
      {
        type: 'FetchAtom',
        appUid,
        data: { ds, pk },
      },
      { appUid },
    )[0]
    .then(async (r) => {
      if (r.status === 'success') {
        if (ds === 'IoPages') {
          if (r.data) {
            const { md, ...others } = r.data as unknown as {
              md: Page | undefined;
              controller?: string;
            };
            platform.setter(pageAtom, md ?? PAGE_404, {
              params: { appUid: pk.appUid, pageId: pk.pageUid },
            });
            platform.setter(['db', ds, pk.pageUid], others);
            try {
              await Controller.compile({
                appUid: pk.appUid,
                pageId: pk.pageUid,
                platform,
                source: others.controller,
                fileName: `${pk.pageUid}_PageController.tsx`,
                debug: platform.isDeveloper(pk.appUid),
              });
            } catch (e) {
              platform.showError(
                `Error compiling page controller: ${getErrorMessage(e)}`,
              );
            } finally {
              setTimeout(() => {
                end();
              }, 600);
            }
            callback();
          }
        } else {
          if (ds === 'IoAppSettings') {
            const appController = r.data?.md?.appController;
            if (appController) {
              try {
                await Controller.compile({
                  appUid: pk.appUid,
                  pageId: 'AppController',
                  platform,
                  source: appController,
                  fileName: `${pk.appUid}_AppController.tsx`,
                  debug: platform.isDeveloper(pk.appUid),
                });
              } catch (e) {
                platform.showError(
                  `Error compiling app controller: ${getErrorMessage(e)}`,
                );
              }
            }
            platform.setter(['db', ds, pk.appUid], r.data);
            platform.setter(contextAppIdAtom, pk.appUid);
            await onAppLoaded(platform, pk.appUid);
          } else if (ds === 'IoUserSettings') {
            platform.setter(['db', ds, pk.userName], r.data);
            Logger.trace('Logger.setEnabled', !!r.data?.md?.clientDebugLog);
            Logger.setEnabled(!!r.data?.md?.clientDebugLog);
            const userDarkMode = r.data?.md?.darkMode ?? 'light';
            if (userDarkMode !== 'system') {
              platform.setter(darkModeAtom, userDarkMode);
            }
            await setupUserAccess(platform, pk.userName);
          }
          callback();
        }
        status = 'success';
        result = r.data as T;
        return result;
      }

      console.error(r);
      status = 'error';
      error = Error(r.message);
      throw error;
    })
    .catch((e) => {
      status = 'error';
      error = e;
      throw e;
    });

  return {
    fetch(cb: Callback = emptyFunction) {
      callback = cb;
      if (status === 'pending') {
        throw promise;
      } else if (status === 'error') {
        throw error;
      }
      return result;
    },
    promise() {
      return promise;
    },
    isResolved() {
      return status !== 'pending';
    },
    ds,
    pk,
  };
}

export default class IOResource implements Resource {
  cache = {};

  platform: Platform;

  currentApp = '';

  switchingApp = '';

  switchingAppResource?: ResourceType<string>;

  lastAppSwitched = '';

  currentPage = '';

  switchingPage = '';

  currentVisitId = '';

  switchingPageResource?: ResourceType<string>;

  constructor(platform: Platform) {
    this.platform = platform;
  }

  get = <T>(
    ds: string,
    appUid: string,
    pk: Record<string, string>,
  ): ResourceType<T> => {
    const spk = stringify(pk);
    let resource: ResourceType<T> | undefined = getIn(
      this.cache,
      ['transient', 'resourceCache', ds, spk],
      undefined,
    );
    if (!resource) {
      resource = wrapPromise<T>(this.platform, ds, appUid, pk);
      this.cache = setIn(
        this.cache,
        ['transient', 'resourceCache', ds, spk],
        resource,
      );
    }
    return resource;
  };

  userSettings = <T>(userName: string) => {
    return this.get<T>('IoUserSettings', 'cloudio', { userName });
  };

  _switchPage = (appUid: string, pageUid: string): ResourceType<string> => {
    let status = 'pending';
    let appSwitched = false;
    const currentVisitId = this.currentVisitId;
    if (appUid !== this.lastAppSwitched) {
      appSwitched = true;
      this.lastAppSwitched = appUid;
    }
    const promise = new Promise<string>((resolve) => {
      this.platform
        .sendRequest(
          {
            type: 'SSA',
            action: 'SwitchPage',
            data: `${appSwitched}:${pageUid}:${currentVisitId}`,
          },
          { appUid, pageUid },
        )[0]
        .then((r) => {
          this.currentVisitId =
            r.status === 'success' &&
            r?.data?.___visit_id_internal_cloudio_tracking;
          this.currentPage = `${appUid}:${pageUid}`;
          resolve(pageUid);
        });
    }).then((r) => {
      status = 'success';
      return r;
    });

    return {
      fetch(_: Callback) {
        if (status === 'pending') {
          throw promise;
        }
        return pageUid;
      },
      promise() {
        return promise;
      },
      isResolved() {
        return status !== 'pending';
      },
      ds: 'IoPages',
      pk: { pageUid },
    };
  };

  switchPage = (appUid: string, pageId: string): ResourceType<string> => {
    if (this.switchingPageResource && pageId === this.switchingPage) {
      return this.switchingPageResource;
    }
    const res = this._switchPage(appUid, pageId);
    this.switchingPage = pageId;
    this.switchingPageResource = res;
    return res;
  };

  _switchApp = (appUid: string): ResourceType<string> => {
    let status = 'pending';
    const promise = new Promise<string>((resolve) => {
      this.platform
        .sendRequest(
          {
            type: 'SSA',
            action: 'SwitchApp',
            data: appUid,
          },
          { appUid },
        )[0]
        .then(async (r) => {
          await onAppSwitched(this.platform, appUid);
          this.platform.setter(contextAppIdAtom, appUid);
          this.currentApp = appUid;
          resolve(appUid);
        });
    }).then(async (r) => {
      status = 'success';
      return r;
    });
    return {
      fetch(_: Callback) {
        if (status === 'pending') {
          throw promise;
        }
        return appUid;
      },
      promise() {
        return promise;
      },
      isResolved() {
        return status !== 'pending';
      },
      ds: 'IoAppSettings',
      pk: { appUid },
    };
  };

  switchApp = (appUid: string): ResourceType<string> => {
    if (this.switchingAppResource && appUid === this.switchingApp) {
      return this.switchingAppResource;
    }
    const res = this._switchApp(appUid);
    this.switchingApp = appUid;
    this.switchingAppResource = res;
    return res;
  };

  appSettings = (appUid: string): ResourceType<string> => {
    const resource = this.get<string>('IoAppSettings', appUid, { appUid });
    if (resource.isResolved()) {
      if (this.currentApp !== appUid) {
        return this.switchApp(appUid);
      }
    } else {
      this.currentApp = appUid;
    }
    return resource;
  };

  page = <T>(appUid: string, pageUid?: string, embedded?: boolean) => {
    if (!pageUid || pageUid === '404') {
      return;
    }
    const resource = this.get<T>('IoPages', appUid, { appUid, pageUid });
    return resource.isResolved() &&
      !embedded &&
      this.currentPage !== `${appUid}:${pageUid}`
      ? this.switchPage(appUid, pageUid)
      : resource;
  };

  clearPage = (appUid: string, pageUid: string): void => {
    const pk = { appUid, pageUid };
    const spk = stringify(pk);
    this.cache = removeIn(this.cache, [
      'transient',
      'resourceCache',
      'IoPages',
      spk,
    ]);
  };

  hasPageLoaded = (appUid: string, pageUid: string) => {
    const pk = { appUid, pageUid };
    const spk = stringify(pk);
    return hasIn(this.cache, ['transient', 'resourceCache', 'IoPages', spk]);
  };

  refreshPage = <T>(appUid: string, pageUid: string, embedded: boolean) => {
    this.clearPage(appUid, pageUid);
    return this.page<T>(appUid, pageUid, embedded);
  };

  destroy = () => {
    this.cache = {};
  };
}
